import React, { useEffect } from 'react';
import SwipeableViews from 'react-swipeable-views';
import { makeStyles, Theme, useTheme } from '@material-ui/core/styles';
import { AppBar, Box, Tab, Tabs, useMediaQuery } from '@material-ui/core';
import RestaurantDetailsTab from '../RestaurantDetailsTab/components/RestaurantDetailsTab';
import RestaurantDetailsCard from '../../components/RestaurantDetailsCard/RestaurantDetailsCard';
import EquipmentTabRestaurant from './EquipmentTabRestaurant/EquipmentTabRestaurant';
import ProductGroupsTab from '../ProductGroupsTab/ProductGroupsTab';
import { useTranslation } from 'react-i18next';
import { RootStateOrAny, useSelector } from 'react-redux';
import { Redirect, useHistory, useLocation } from 'react-router-dom';
import { useFlags } from 'launchdarkly-react-client-sdk';
import OperationHoursMaster from '../OperationHoursMaster/OperationHoursMaster';
import OperationHoursTab from '../OperationHoursTab/OperationHoursTabOld';
import { shouldEnableNewWeekView } from '../../utils/newWeekViewFeatureFlags';

interface TabPanelProps {
  children?: React.ReactNode;
  dir?: string;
  index: any;
  value: any;
  id: string;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, id, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`full-width-tabpanel-${index}`}
      aria-labelledby={`full-width-tab-${index}`}
      {...other}
    >
      {value === index && <Box p={3}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: any) {
  return {
    id: `full-width-tab-${index}`,
    'aria-controls': `full-width-tabpanel-${index}`,
  };
}

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    backgroundColor: theme.palette.background.paper,
    width: '100%',
  },
  subMenusColor: {
    backgroundColor: theme.palette.subMenusColor.main,
    color: theme.palette.constrastGreyBlack.main,
  },
  tabs: {
    fontSize: 12,
    textTransform: 'none',
  },
}));

const options = {
  info: 0,
  equipments: 1,
  operation: 2,
  products: 3,
  commodities: 4,
};
const optionsReverse = {
  '0': 'info',
  '1': 'equipments',
  '2': 'operation',
  '3': 'products',
  '4': 'commodities',
};

export default function RestaurantDetailsTabs({ id, tab = 'equipments' }) {
  const classes = useStyles();
  const theme = useTheme();
  const [value, setValue] = React.useState(0);
  const matches = useMediaQuery(theme.breakpoints.down('sm'));
  const history = useHistory();
  const location = useLocation();
  const handleChange = (
    event: React.ChangeEvent<unknown>,
    newValue: number,
  ) => {
    setValue(newValue);
    history.push(`/restaurants/${id}/${optionsReverse[newValue]}`);
  };

  const { thisUser } = useSelector(
    (state: RootStateOrAny) => state.UserReducer,
  );
  const { newWeekViewFlags } = useFlags();
  const state = useSelector((state: RootStateOrAny) => state);
  const isNewWeekViewEnabled = shouldEnableNewWeekView(state, newWeekViewFlags);
  useEffect(() => {
    if (typeof tab !== 'undefined') {
      setValue(options[tab]);
    }
  }, [tab]);

  const { t } = useTranslation();
  if (
    !thisUser.hasViewCommodities &&
    location.pathname === `/restaurants/${id}/commodities`
  ) {
    return <Redirect to={`/`} />;
  }
  const tabsDesktop = (
    <Tabs
      value={value}
      onChange={handleChange}
      className={classes.subMenusColor}
      indicatorColor="secondary"
      textColor="inherit"
      variant="fullWidth"
      aria-label="full width tabs example"
    >
      {
        <Tab
          className={classes.tabs}
          label={t('app.nav.restaurant.restaurantInfo')}
          {...a11yProps(0)}
        />
      }
      <Tab
        className={classes.tabs}
        label={t('app.nav.restaurant.equipment')}
        {...a11yProps(1)}
        style={
          thisUser.hasViewEquipment || thisUser.hasEditEquipment
            ? undefined
            : { display: 'none' }
        }
      />
      <Tab
        className={classes.tabs}
        label={t('app.nav.restaurant.operationHours')}
        {...a11yProps(2)}
        style={
          thisUser.hasViewHoursOfOperation || thisUser.hasEditHoursOfOperation
            ? undefined
            : { display: 'none' }
        }
      />
      <Tab
        className={classes.tabs}
        label={t('app.nav.restaurant.productGroups')}
        {...a11yProps(3)}
        style={
          thisUser.hasViewMenuItemList ||
          thisUser.hasViewMenuItemDetails ||
          thisUser.hasViewMenuItemListAndDetails ||
          thisUser.hasSearchMenuItem
            ? undefined
            : { display: 'none' }
        }
      />
      <Tab
        className={classes.tabs}
        label={t('app.nav.restaurant.commodities')}
        {...a11yProps(4)}
        style={thisUser.hasViewCommodities ? undefined : { display: 'none' }}
      />
    </Tabs>
  );
  const tabsTablets = (
    <Tabs
      value={value}
      onChange={handleChange}
      className={classes.subMenusColor}
      indicatorColor="secondary"
      textColor="inherit"
      variant="scrollable"
      scrollButtons="auto"
      aria-label=" tabs example"
    >
      {
        <Tab
          className={classes.tabs}
          label={t('app.nav.restaurant.restaurantInfo')}
          {...a11yProps(0)}
        />
      }
      <Tab
        className={classes.tabs}
        label={t('app.nav.restaurant.equipment')}
        {...a11yProps(1)}
        style={
          thisUser.hasViewEquipment || thisUser.hasEditEquipment
            ? undefined
            : { display: 'none' }
        }
      />
      <Tab
        className={classes.tabs}
        label={t('app.nav.restaurant.operationHours')}
        {...a11yProps(2)}
        style={
          thisUser.hasViewHoursOfOperation || thisUser.hasEditHoursOfOperation
            ? undefined
            : { display: 'none' }
        }
      />
      <Tab
        className={classes.tabs}
        label={t('app.nav.restaurant.productGroups')}
        {...a11yProps(3)}
        style={
          thisUser.hasViewMenuItemList ||
          thisUser.hasViewMenuItemDetails ||
          thisUser.hasViewMenuItemListAndDetails ||
          thisUser.hasSearchMenuItem
            ? undefined
            : { display: 'none' }
        }
      />
      <Tab
        className={classes.tabs}
        label={t('app.nav.restaurant.commodities')}
        {...a11yProps(4)}
        style={thisUser.hasViewCommodities ? undefined : { display: 'none' }}
      />
    </Tabs>
  );
  return (
    <div className={classes.root}>
      <AppBar position="static" color="primary">
        {matches ? tabsTablets : tabsDesktop}
      </AppBar>
      {(isNewWeekViewEnabled ? value !== 2 : true) && <RestaurantDetailsCard />}
      <SwipeableViews
        axis={theme.direction === 'rtl' ? 'x-reverse' : 'x'}
        index={value}
        onChangeIndex={handleChange}
      >
        <TabPanel value={value} key={0} index={0} dir={theme.direction} id={id}>
          <RestaurantDetailsTab />
        </TabPanel>
        {thisUser.hasViewEquipment || thisUser.hasEditEquipment ? (
          <TabPanel
            value={value}
            key={1}
            index={1}
            dir={theme.direction}
            id={id}
          >
            <EquipmentTabRestaurant restaurantId={id} />
          </TabPanel>
        ) : null}
        {thisUser.hasViewHoursOfOperation ||
        thisUser.hasEditHoursOfOperation ? (
          <TabPanel
            value={value}
            key={2}
            index={2}
            dir={theme.direction}
            id={id}
          >
            {isNewWeekViewEnabled ? (
              <OperationHoursMaster />
            ) : (
              <OperationHoursTab />
            )}
          </TabPanel>
        ) : null}
        {thisUser.hasViewMenuItemList ||
        thisUser.hasViewMenuItemDetails ||
        thisUser.hasViewMenuItemListAndDetails ||
        thisUser.hasSearchMenuItem ? (
          <TabPanel
            value={value}
            key={3}
            index={3}
            dir={theme.direction}
            id={id}
          >
            <ProductGroupsTab restaurantId={id} />
          </TabPanel>
        ) : null}
        {thisUser.hasViewCommodities ? (
          <TabPanel
            value={value}
            key={4}
            index={4}
            dir={theme.direction}
            id={id}
          >
            {t('app.nav.restaurant.commodities')}
          </TabPanel>
        ) : null}
      </SwipeableViews>
    </div>
  );
}
